import React, { useState } from 'react';
import { DateTime } from 'luxon';
import {
  Card,
  Col,
  Row,
  Space,
  Checkbox,
  ChevronGlyphDown,
  Spin,
  ChevronGlyphUp,
} from 'syngenta-digital-cropwise-react-ui-kit';
import place from 'assets/place_24px.svg';
import perm_identity_24px from 'assets/perm_identity_24px.svg';
import calendar_today_24px from 'assets/calendar_today_24px.svg';
import measureOptions from './MeasureOptions';
import { sentenceCase, DataRow, formatNumber } from 'services/utils';
import { convertUom, parseUom } from 'services/uom';
import { ResourceType, Instance, Product } from '../types';
import { useTranslation } from 'react-i18next';
import { getAreaTotalFromOperation } from 'utils/operation.utils';

import {
  AmplitudeEventEnum,
  AmplitudePropertyKeyEnum,
  useAmplitude,
} from '../../../services/amplitudeHelper';
import { useResourceMapperStore } from '../store/useResourceMapperStore';
import { APIState } from '../../../models/APIState';
import { useUnitSystemStore } from '../../../modules/yield-analysis/store/useUnitSystemStore';

type InstanceCardProps = {
  instance: Instance;
  activeResourceType: ResourceType | undefined;
  checked: boolean;
  onChange: any;
  icon: any;
  operationType: string;
  instanceProductsMap: Map<string, Product[]>;
  loadInstanceProductsStatus: APIState;
};

export default function InstanceCard({
  instance,
  checked,
  onChange,
  icon,
  operationType,
  instanceProductsMap,
  loadInstanceProductsStatus,
}: InstanceCardProps): JSX.Element {
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const { selectedIntegration, getProductsByOperationId } = useResourceMapperStore();
  const { unitSystem } = useUnitSystemStore();

  const trackEvent = useAmplitude();

  const trackExpandOperationCard = () => {
    trackEvent(AmplitudeEventEnum.OPEN_OPERATION_DETAILS, {
      [AmplitudePropertyKeyEnum.INTEGRATION_NAME]: selectedIntegration,
    });
  };

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
    collapsed && trackExpandOperationCard();
  };
  const formatDate = (og_date_format: string) => {
    return og_date_format ? DateTime.fromISO(og_date_format).toFormat(`d MMM yyyy', ' h:mm a`) : '';
  };
  const { t } = useTranslation();
  const area_object = getAreaTotalFromOperation(instance.parent_operation);
  const area = convertUom(area_object?.value, area_object?.uom, 'ac') ?? 0;

  const hasProducts = instanceProductsMap.has(instance.parent_operation.id);
  const products = instanceProductsMap.get(instance.parent_operation.id);
  const isLoadingProducts = loadInstanceProductsStatus === APIState.LOADING;

  if (!hasProducts && !isLoadingProducts && !collapsed) {
    void getProductsByOperationId(instance.parent_operation.id, unitSystem);
  }

  const getMeasures = (measures: any[]) => {
    return measures.map((measure: any) => {
      const measureOption = measureOptions.find(({ value }: any) => measure.dtr_code === value);
      const value = parseFloat(measure.value);
      let uom = measure.uom;
      let valueOut, uomOut;
      switch (measure.dtr_code) {
        case 'A_AREA_TOTAL':
        case operationType === 'APPLICATION' && 'A_TGT_VOL_PER_AREA':
          return null;
        case 'A_AREA_ALLOCATED':
          uomOut = 'ac';
          valueOut = convertUom(value, uom, uomOut) ?? 0;
          valueOut = formatNumber(valueOut, valueOut >= 1 ? 0 : 1);
          uom = uomOut;
          break;
        case 'A_YLD_MOISTURE':
          valueOut = formatNumber(value, 1);
          break;
        case 'A_YLD_VOL_TOTAL':
          uomOut = 'bu';
          valueOut = formatNumber(convertUom(value, uom, uomOut), 0);
          uom = uomOut;
          break;
        case 'A_YLD_VOL_PER_AREA':
          uomOut = 'bu1ac-1';
          valueOut = formatNumber(convertUom(value, uom, uomOut), 0);
          uom = uomOut;
          break;
        default:
          valueOut = formatNumber(value);
      }

      const title = measureOption ? `${measureOption.label}:` : null;
      return (
        <DataRow
          key={measure.id}
          title={title}
          value={
            <>
              {valueOut} {parseUom(uom)}
            </>
          }
        />
      );
    });
  };

  const getDescriptionForMixProduct = (product: Product) => {
    if (product.product_type !== 'MIX') return;

    const value = product.product_components.reduce(
      (acc: string[], value: Product['product_components'][0]) => {
        const description = products?.find(
          (product: Product) => product.product_id === value.product_ref,
        )?.description;

        return description ? [...acc, description] : acc;
      },
      [],
    );

    return (
      <DataRow
        key="mix"
        title="Ingredients: "
        value={<span data-cy="mix_ingredients_label">{value.join(', ')}</span>}
      />
    );
  };

  const sentenceType = sentenceCase(instance.parent_operation.operation_type);
  const translatedType = t(sentenceType);
  const formattedStartDate = formatDate(instance.parent_operation.start_date_time);
  const translatedStartDate = t(formattedStartDate);
  const formattedEndDate = formatDate(instance.parent_operation.end_date_time);
  const translatedEndDate = t(formattedEndDate);

  return (
    <Card
      className="instance-card shadow-sm-card rounded-4 mb-8 w-100%"
      data-testid={`instance-card-${instance.id}`}
    >
      <Space direction="vertical" className="w-100%">
        <Row justify="space-between" className="pl-8 pr-4">
          <Col>
            <Checkbox
              data-cy={`${instance.parent_operation.start_date_time}_checkbox`}
              checked={checked}
              onChange={onChange}
            >
              <div className="flex justify-start items-center pl-4">
                <img
                  className="h-20px mr-10"
                  src={icon}
                  alt={`${instance.parent_operation.operation_type} icon`}
                />
                <div className="h-20px mr-10 font-normal text-gray">{translatedType}</div>
                <span style={{ fontWeight: checked ? 'bold' : 500 }}>{translatedStartDate}</span>
                {!!area_object && (
                  <span className="ml-8">{formatNumber(area, area >= 1 ? 0 : 1)} ac</span>
                )}
              </div>
            </Checkbox>
          </Col>
          <Col className="items-right">
            <button
              onClick={toggleCollapse}
              data-testid="collapse-button"
              className="flex items-center h-100%"
            >
              {collapsed ? <ChevronGlyphDown /> : <ChevronGlyphUp />}
            </button>
          </Col>
        </Row>
        {collapsed ? null : (
          <>
            <div className="divider" style={{ margin: '0 -12px' }}></div>
            <div className="py-4 px-8 space-y-2">
              <div
                className="w-full flex flex-row justify-start items-center"
                data-testid="expanded-section"
              >
                <span className="h-[20px] w-[20px] flex flex-col justify-center items-center mr-[5px]">
                  <img src={place} alt="" className="p-2" />
                </span>
                <h1 className="font-normal text-neutral-60">{`${
                  instance.parent_operation.organization_value ?? 'Missing Org'
                } / ${instance.parent_operation.farms?.[0]?.description ?? 'Missing Farm'} / ${
                  instance.parent_operation.fields?.[0]?.description ?? 'Missing Field'
                }`}</h1>
              </div>

              <div className="w-full flex flex-row justify-start items-start">
                <span className="h-[20px] w-[20px] flex flex-col justify-center items-center mr-[5px] pt-7">
                  <img src={calendar_today_24px} alt="calendar icon" className="" />
                </span>
                <div className="flex flex-col justify-start items-start">
                  <div className="flex flex-row justify-start items-center">
                    <b className="mr-2 leading-4 font-normal text-gray">
                      {t('timeline-analytics.start')}{' '}
                    </b>{' '}
                    {translatedStartDate}
                  </div>
                  <div className="flex flex-row justify-start items-center">
                    <b className="mr-2 font-normal text-gray">{t('timeline-analytics.end')} </b>{' '}
                    {translatedEndDate}
                  </div>
                </div>
              </div>

              <div className="w-full flex flex-row justify-start items-start">
                <span className="h-[20px] w-[20px] flex flex-col justify-center items-center mr-[5px]">
                  <img src={perm_identity_24px} className="p-2" alt="" />
                </span>
                <div className="flex flex-row justify-start items-center">
                  <b className="mr-2 font-normal text-gray">{t('timeline-analytics.operator')} </b>{' '}
                  {instance.parent_operation?.parties?.[0]?.name}
                </div>
              </div>

              {isLoadingProducts && !hasProducts ? (
                <div
                  data-cy="instance_card_list_loading"
                  data-testid="instance_card_list_loading"
                  className="h-100% w-100% text-center"
                >
                  <Spin size="small" spinning />
                </div>
              ) : (
                <div className="flex flex-wrap">
                  {products
                    ?.filter((product) => product.mix_measures?.length)
                    ?.map((product) => (
                      <div
                        className={`flex justify-start items-start p-8 rounded-4 flex-col text-left m-2${
                          product.is_resolved ? ' border-1 border-blue-60' : ''
                        }`}
                      >
                        <b className="mr-2">{product.description}</b>
                        {getMeasures(product.mix_measures!)}
                      </div>
                    ))}

                  {products
                    ?.filter((product) => product.measures?.length)
                    ?.map((product) => (
                      <div
                        className={`flex justify-start items-start p-8 rounded-4 flex-col m-2${
                          product.is_resolved ? ' border-1 border-blue-60' : ''
                        }`}
                      >
                        <b className="mr-2">
                          {product.product_type === 'MIX' && product.description === 'UNKNOWN'
                            ? t('resource-mapper.tank_mix')
                            : product.description}
                        </b>
                        {getMeasures(product.measures!)}
                        {getDescriptionForMixProduct(product)}
                      </div>
                    ))}
                </div>
              )}
            </div>
          </>
        )}
      </Space>
    </Card>
  );
}
